'use client';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import { ZSHOP_PICTURE } from '@eliadmin/core/src/components/product/detail/product-images';
import { ProductQuantity } from '@eliadmin/core/src/components/product/detail/product-quantity';
import { ProductTabAccessories } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-accessories';
import { ProductTabAttachments } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-attachments';
import { ProductTabColorVariants } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-color-variants';
import { ProductTabCuts } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-cuts';
import { ProductTabDescription } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-description';
import { ProductTabParameters } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-parameters';
import { ProductTabQuestionForm } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-quetsion-form';
import { ProductTabReviews } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-reviews';
import { ProductTabSuppliers } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-suppliers';
import { ProductTabTechnical } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-technical';
import { ProductTabVariants } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-variants';
import { ProductTabVideo } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-video';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useProductColorVariants } from '@eliadmin/core/src/hooks/proform-product-color-variants';
import { useProductConfVariants } from '@eliadmin/core/src/hooks/proform-product-conf';
import { useProductOrnament } from '@eliadmin/core/src/hooks/proform-product-ornaments';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { encodeProformSlug, getIdNomenklaturyFrom } from '@eliadmin/core/src/utils/proform-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buildConfiguratorHref } from '@eliadmin/core/src/utils/configurator-route-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { useState, type PropsWithChildren } from 'react';
import { LuSettings } from 'react-icons/lu';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export enum ProductTab {
  DESCRIPTION = 'description',
  SUPPLIERS = 'suppliers',
  PARAMETERS = 'parameters',
  REVIEWS = 'reviews',
  ACCESSORIES = 'accessories',
  VIDEO = 'video',
  ATTACHMENTS = 'attachments',
  TECHNICAL = 'technical',
  QUESTION_FORM = 'question_form',
  CUTS = 'cuts',
  VARIANTS = 'variants',
  COLOR_VARIANTS = 'color-variants',
}

type ProductTabsProps = {
  className?: string;
  product?: ProductDetailData;
};

function ProductTabs({ className, children, product }: PropsWithChildren<ProductTabsProps>) {
  const firstEnabled = ProductTab.TECHNICAL;
  const me = useCustomerStore(s => s.me);
  const { t } = useTranslations();
  const pathname = usePathname();

  const idNomenklatury = product && getIdNomenklaturyFrom(product);
  const colsQuery = useQuery({
    queryKey: [idNomenklatury, 'GET_PRODUCT_CONF_COLS'],
    queryFn: () =>
      proformApiQuery('GET_PRODUCT_CONF_COLS', {
        cislo_nomenklatury: idNomenklatury!,
        id_shop: 3,
        ...(me?.customer.email ? { session_e_mail: me.customer.email } : {}),
      }).catch(() => null),
    enabled: Boolean(idNomenklatury),
    retry: false,
  });

  const [activeTab, setActiveTab] = useState<ProductTab>(firstEnabled || ProductTab.DESCRIPTION);

  return (
    <Tabs
      defaultValue={firstEnabled}
      className={cn('ea-product-detail-tabs grid grid-cols-12 gap-4', className)}
      onValueChange={e => setActiveTab(e as ProductTab)}
      value={activeTab!}
    >
      <div className="order-2 col-span-12 md:order-1 md:col-span-8">{children}</div>
      <div className="order-1 col-span-12 md:order-2 md:col-span-4">
        <Card className="relative bg-muted md:-top-[90px]">
          <CardContent className="flex flex-col items-center justify-center gap-2 p-6">
            <div className="w-full">
              {colsQuery.isPending ? (
                <div className="flex h-12 w-full justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  {!colsQuery.data?.root?.smako_conf_cols ? (
                    <>
                      <div className="flex w-full flex-row items-center justify-end">
                        <ProductPrice product={product as any} className={{ main: 'items-end' }} />
                      </div>
                      <ProductQuantity />
                    </>
                  ) : (
                    !!idNomenklatury && (
                      <Button
                        // TODO: bg vars
                        className="w-full gap-2 rounded-full bg-green-600 text-white hover:bg-green-600/80"
                        size="lg"
                        asChild
                      >
                        <Link
                          href={buildConfiguratorHref(
                            `/configurator/${encodeProformSlug(idNomenklatury)}`,
                            { returnPath: pathname },
                          )}
                        >
                          <LuSettings /> {t('text1470')}
                        </Link>
                      </Button>
                    )
                  )}
                </>
              )}
            </div>

            <Button
              variant={'outline'}
              className="w-full rounded-full border-muted-foreground"
              size="sm"
              onClick={() => setActiveTab(ProductTab.QUESTION_FORM)}
            >
              {t('text1038')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Tabs>
  );
}

type ProductTabsListProps = {
  className?: string;
};

function ProductTabsList(props: PropsWithChildren<ProductTabsListProps>) {
  return (
    <div className="w-full">
      <div className="hide-scroll-bar m-auto w-full max-w-screen-xl overflow-x-scroll px-4">
        <TabsList
          className={cn(
            'hide-scroll-bar box-border justify-start overflow-visible rounded-none bg-transparent p-0',
            'ea-product-detail-tabs__list',
            props.className,
          )}
        >
          {props.children}
        </TabsList>
      </div>
    </div>
  );
}

type ProductTabsTriggersProps = {
  className?: string;
};

function ProductTabsTriggers({ className }: ProductTabsTriggersProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const { t } = useTranslations();
  const product = useVariantsStore(s => s.product) as ProductDetailsWithSlug & {
    images: { metadata: { type: { type: string } } }[];
  };

  const variants = useProductConfVariants(product.code);
  const colorVariants = useProductColorVariants(product.code);
  const ornaments = useProductOrnament(product.code);

  const hasCuts = product?.images?.some(
    (image: any) => image?.metadata?.type === ZSHOP_PICTURE.REZ,
  );

  const hasVariants = ornaments.data?.length || variants.data?.length;
  const hasColorVariants = colorVariants.data?.length;

  className = cn(
    'mr-1 h-full rounded-none rounded-t-sm bg-transparent shadow-none text-xs transition-none',
    'text-[hsl(var(--primary))]',
    'data-[state=active]:text-black data-[state=active]:shadow-none',
    className,
  );

  return (
    <>
      {!!templateSettings.product_detail?.description?.enabled && !!product.description && (
        <TabsTrigger
          value={ProductTab.DESCRIPTION}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__description-trigger',
            className,
          )}
        >
          {t('text231')}
        </TabsTrigger>
      )}
      <TabsTrigger
        value={ProductTab.TECHNICAL}
        className={cn(
          'text-left text-[14px]',
          'ea-product-detail-tabs__technical-trigger',
          className,
        )}
      >
        {t('text1034')}
      </TabsTrigger>
      {hasCuts && (
        <TabsTrigger
          value={ProductTab.CUTS}
          className={cn('text-left text-[14px]', 'ea-product-detail-cuts-trigger', className)}
        >
          {t('text1045')}
        </TabsTrigger>
      )}
      {templateSettings.product_detail?.parameters?.enabled && !!product.attributes?.length && (
        <TabsTrigger
          value={ProductTab.PARAMETERS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__paremeters-trigger',
            className,
          )}
        >
          {t('text9')}
        </TabsTrigger>
      )}
      {hasVariants && (
        <TabsTrigger
          value={ProductTab.VARIANTS}
          className={cn('text-left text-[14px]', 'ea-product-detail-variants-trigger', className)}
        >
          {t('text826')}
        </TabsTrigger>
      )}
      {hasColorVariants && (
        <TabsTrigger
          value={ProductTab.COLOR_VARIANTS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-color-variants-trigger',
            className,
          )}
        >
          {t('text1052')}
        </TabsTrigger>
      )}
      {templateSettings.product_detail?.video?.enabled && !!product.video && (
        <TabsTrigger
          value={ProductTab.VIDEO}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__video-trigger',
            className,
          )}
        >
          {t('text451')}
        </TabsTrigger>
      )}
      {templateSettings.product_detail?.accessories?.enabled && !!product.accessories?.length && (
        <TabsTrigger
          value={ProductTab.ACCESSORIES}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__accesories-trigger',
            className,
          )}
        >
          {t('text232')}
        </TabsTrigger>
      )}
      {/* {templateSettings.product_detail.attachments.enabled && !!product.attachments?.length && ( */}
      {!!product.attachments?.length && (
        <TabsTrigger
          value={ProductTab.ATTACHMENTS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__accesories-trigger',
            className,
          )}
        >
          {t('text333')}
        </TabsTrigger>
      )}
      {templateSettings.product_detail?.suppliers?.enabled && (
        <TabsTrigger
          value={ProductTab.SUPPLIERS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__accesories-trigger',
            className,
          )}
        >
          {t('text718')}
        </TabsTrigger>
      )}
      {product.text_fields?.map(
        (field, index) =>
          field.value && (
            <TabsTrigger
              value={field.name}
              key={index}
              className={cn(
                'text-left text-[14px]',
                'ea-product-detail-tabs__video-trigger',
                className,
              )}
            >
              {t(`${field.name}`)}
            </TabsTrigger>
          ),
      )}
      <TabsTrigger
        value={ProductTab.QUESTION_FORM}
        className={cn(
          'text-left text-[14px]',
          'ea-product-detail-tabs__technical-trigger',
          className,
        )}
      >
        {t('text1038')}
      </TabsTrigger>
      {templateSettings.product_detail?.reviews?.enabled && (
        <TabsTrigger
          value={ProductTab.REVIEWS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__reviews-trigger',
            className,
          )}
        >
          {t('text447')}
        </TabsTrigger>
      )}
    </>
  );
}

type ProductTabsContentProps = {
  className?: string;
};

function ProductTabsContent(props: ProductTabsContentProps) {
  const product = useVariantsStore(s => s.product);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const { t } = useTranslations();
  return (
    <div className="w-full">
      <div
        className={cn(
          'm-auto w-full max-w-screen-xl sm:px-4',
          'ea-product-detail-tabs__content',
          props.className,
        )}
      >
        {templateSettings.product_detail?.description?.enabled && !!product.description && (
          <TabsContent
            value={ProductTab.DESCRIPTION}
            className={cn('mr-0', 'ea-product-detail-tabs__description')}
          >
            <ProductTabDescription />
          </TabsContent>
        )}
        <TabsContent
          value={ProductTab.TECHNICAL}
          className={cn('mr-0', 'ea-product-detail-tabs__techical')}
        >
          <ProductTabTechnical />
        </TabsContent>
        {templateSettings.product_detail?.parameters?.enabled && !!product.attributes?.length && (
          <TabsContent
            value={ProductTab.PARAMETERS}
            className={cn('mr-0', 'ea-product-detail-tabs__parameters')}
          >
            <ProductTabParameters />
          </TabsContent>
        )}
        {templateSettings.product_detail?.reviews?.enabled && (
          <TabsContent
            value={ProductTab.REVIEWS}
            className={cn('mr-0', 'ea-product-detail-tabs__reviews')}
          >
            <ProductTabReviews />
          </TabsContent>
        )}
        {templateSettings.product_detail?.video?.enabled && !!product.video && (
          <TabsContent
            value={ProductTab.VIDEO}
            className={cn('mr-0', 'ea-product-detail-tabs__video')}
          >
            <ProductTabVideo />
          </TabsContent>
        )}
        {templateSettings.product_detail?.accessories?.enabled && !!product.accessories?.length && (
          <TabsContent
            value={ProductTab.ACCESSORIES}
            className={cn('mr-0', 'ea-product-detail-tabs__accesories')}
          >
            <ProductTabAccessories />
          </TabsContent>
        )}
        {/* {templateSettings.product_detail.attachments.enabled && !!product.attachments?.length && ( */}
        {!!product.attachments?.length && (
          <TabsContent
            value={ProductTab.ATTACHMENTS}
            className={cn('mr-0', 'ea-product-detail-tabs__attachments')}
          >
            <ProductTabAttachments />
          </TabsContent>
        )}
        {templateSettings.product_detail?.suppliers?.enabled && (
          <TabsContent
            value={ProductTab.SUPPLIERS}
            className={cn('mr-0', 'ea-product-detail-tabs__suppliers')}
          >
            <ProductTabSuppliers />
          </TabsContent>
        )}
        <TabsContent
          value={ProductTab.QUESTION_FORM}
          className={cn('mr-0', 'ea-product-detail-tabs__technical')}
        >
          <ProductTabQuestionForm />
        </TabsContent>{' '}
        <TabsContent value={ProductTab.CUTS} className={cn('mr-0', 'ea-product-detail-tabs__cuts')}>
          <ProductTabCuts />
        </TabsContent>
        <TabsContent
          value={ProductTab.VARIANTS}
          className={cn('mr-0', 'ea-product-detail-tabs__variants')}
        >
          <ProductTabVariants />
        </TabsContent>
        <TabsContent
          value={ProductTab.COLOR_VARIANTS}
          className={cn('mr-0', 'ea-product-detail-tabs__color-variants')}
        >
          <ProductTabColorVariants />
        </TabsContent>
        {product.text_fields?.map(
          (field, index) =>
            field.value && (
              <TabsContent
                value={field.name}
                key={index}
                className={cn('mr-0', 'ea-product-detail-tabs__accesories')}
              >
                <Container
                  className={cn('flex flex-col px-2', 'ea-product-detail-tab-description')}
                >
                  <h2 className="mb-4 text-2xl font-bold">{t(`${field.name}`)}</h2>
                  <HtmlRendererRaw html={field.value} />
                </Container>
              </TabsContent>
            ),
        )}
      </div>
    </div>
  );
}

export { ProductTabs, ProductTabsContent, ProductTabsList, ProductTabsTriggers };
